import { CreditStatusEnum, CreditTypeEnum } from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  CreditStatusEnumDisplay,
  CreditTypeEnumDisplay,
} from '../../../constants/credits';
import {
  getAccountCreditNoteViewRoute,
  getAccountCreditsViewRoute,
  getBillGroupPageRoute,
} from '../../../constants/routes';
import { ICreditWithBillGroup } from '../../../hooks/AccountDetail/useCredits';

const columnHelper = createColumnHelper<ICreditWithBillGroup>();

const statusFilterValues = getColumnFilterValuesForEnum(
  CreditStatusEnum,
  CreditStatusEnumDisplay,
);

const typeFilterValues = getColumnFilterValuesForEnum(
  CreditTypeEnum,
  CreditTypeEnumDisplay,
);

export interface CreditsListColumnContext extends ColumnContextBase<ICreditWithBillGroup> {
  accountId: string;
}

/** tableId: local filter state (no FilterStateKeys) */
export const CREDITS_LIST_CATALOG: ColumnCatalog<
  ICreditWithBillGroup,
  CreditsListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: ({ accountId }) =>
      columnHelper.display({
        id: 'name',
        ...getColHeaderWithSize('Name', 250),
        cell: ({ row }) => {
          const credit = row.original;
          if (credit.type === CreditTypeEnum.CREDIT_NOTE) {
            return nameWithIdBodyTemplate<ICreditWithBillGroup, any>({
              nameProp: 'name',
              idProp: 'creditNoteId',
              idLinkFn: (id) => getAccountCreditNoteViewRoute(accountId, id),
            })({
              row,
              getValue: () => credit.name,
            } as any);
          }

          return nameWithIdBodyTemplate<ICreditWithBillGroup, any>({
            nameProp: 'name',
            idProp: 'id',
            idLinkFn: (id) => getAccountCreditsViewRoute(accountId, id),
          })({
            row,
            getValue: () => credit.name,
          } as any);
        },
        enableSorting: true,
      }),
  },
  {
    id: 'billGroupId',
    label: 'Bill Group',
    build: () =>
      columnHelper.accessor('billGroupId', {
        ...getColHeaderWithSize('Bill Group', 200),
        cell: nameWithIdBodyTemplate<ICreditWithBillGroup, any>({
          nameProp: 'billGroup.name',
          idProp: 'billGroup.id',
          idLinkFn: (id) => getBillGroupPageRoute(id),
          fallback: '',
        }),
        enableSorting: false,
      }),
  },
  {
    id: 'type',
    label: 'Type',
    build: () =>
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Type', 150),
        cell: enumDisplayBodyTemplate<ICreditWithBillGroup, CreditTypeEnum>({
          displayMap: CreditTypeEnumDisplay,
        }),
        enableSorting: true,
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: typeFilterValues,
        }),
      }),
  },
  {
    id: 'expirationDate',
    label: 'Expiration',
    build: () =>
      columnHelper.accessor('expirationDate', {
        ...getColHeaderWithSize('Expiration', 150),
        cell: ({ row }) => {
          const credit = row.original;
          return (
            <MText truncate lineClamp={1}>
              {credit.expirationDate
                ? toDateShort(credit.expirationDate)
                : 'N/A'}
            </MText>
          );
        },
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        cell: ({ row }) => {
          const credit = row.original;
          if (credit.status === CreditStatusEnum.VOID) {
            return (
              <MText
                color="tRed.base"
                lineClamp={1}
                maxW="fit-content"
                h="18px"
                lineHeight="18px"
              >
                {CreditStatusEnumDisplay[credit.status]}
              </MText>
            );
          }

          return enumDisplayBodyTemplate<
            ICreditWithBillGroup,
            CreditStatusEnum
          >({
            displayMap: CreditStatusEnumDisplay,
          })({
            row,
            getValue: () => credit.status,
          } as any);
        },
        enableSorting: true,
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: statusFilterValues,
        }),
      }),
  },
  {
    id: 'amount',
    label: 'Amount',
    build: () =>
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Amount', 150),
        cell: currencyBodyTemplate<ICreditWithBillGroup, number>({
          currencyProperty: 'currency',
        }),
        enableSorting: true,
        meta: {
          cellStyles: {
            textAlign: 'right',
          },
        },
      }),
  },
  {
    id: 'amountRemaining',
    label: 'Amount Remaining',
    build: () =>
      columnHelper.accessor('amountRemaining', {
        ...getColHeaderWithSize('Amount Remaining', 200),
        cell: ({ row }) => {
          const credit = row.original;
          if (credit.status === CreditStatusEnum.VOID) {
            return (
              <MText lineClamp={1}>
                {formatCurrency(0, { currency: credit.currency })}
              </MText>
            );
          }

          return currencyBodyTemplate<ICreditWithBillGroup, number>({
            currencyProperty: 'currency',
          })({
            row,
            getValue: () => credit.amountRemaining,
          } as any);
        },
        enableSorting: true,
        meta: {
          cellStyles: {
            textAlign: 'right',
          },
        },
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        // ...getColHeaderFilters('DATE'), // BE blows up with 500
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        // ...getColHeaderFilters('DATE'), // BE blows up with 500
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<ICreditWithBillGroup>(),
];
